import styled, { css } from 'styled-components';

import { Caption } from '@/shared/ui/caption';
import { Icon } from '@/shared/ui/icon';

import { InputProps, StyledInputProps } from './input.types';

export const Wrapper = styled.label<
	Pick<InputProps, 'maxWidth'> & Required<Pick<InputProps, 'variant'>> & { hasValue?: boolean }
>`
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	position: relative;
	max-width: ${({ maxWidth, theme: { input } }) => maxWidth && input.width[maxWidth]};

	& svg {
		fill: ${({ hasValue, variant, theme: { input } }) => hasValue && input[variant].focus.color};
	}

	&:focus-within {
		& svg {
			fill: ${({ variant, theme: { input } }) => input[variant].focus.color};
		}
	}
`;

export const Input = styled.input<StyledInputProps>`
	outline: none;
	padding: ${({ adornment, endAdornment }) => {
		if (adornment) return '8px 16px 8px 42px';
		if (endAdornment) return '8px 42px 8px 16px';
		return '8px 16px';
	}};
	border: 1px solid
		${({ variant, error, theme: { input } }) => (error?.show ? input[variant].error.border : input[variant].border)};
	background-color: ${({ variant, error, theme: { input } }) =>
		error?.show ? input[variant].error.background : input[variant].background};
	color: ${({ variant, error, theme: { input } }) =>
		error?.show ? input[variant].error.color : input[variant].fontColor};
	-webkit-text-fill-color: ${({ variant, error, theme: { input } }) =>
		error?.show ? input[variant].error.color : input[variant].fontColor};
	width: 100%;
	font-size: 15px;
	border-radius: ${({ variant, theme: { input } }) => input[variant].radius};
	margin-bottom: ${({ paragraph }) => paragraph};

	&:-webkit-autofill,
	&:-webkit-autofill:hover,
	&:-webkit-autofill:focus,
	&:-webkit-autofill:active {
		-webkit-box-shadow: ${({ variant, error, theme: { input } }) =>
			`0 0 0 30px ${error?.show ? input[variant].error.background : input[variant].background} inset`};
	}

	${({ hasValue, variant, error, theme: { input } }) =>
		hasValue &&
		!error?.show &&
		css`
			color: ${input[variant].filled.color};
			-webkit-text-fill-color: ${input[variant].filled.color};
		`}

	&:focus {
		border: 1.5px solid
			${({ variant, error, theme: { input } }) =>
				error?.show ? input[variant].error.border : input[variant].focus.border};
		color: ${({ variant, error, theme: { input } }) =>
			error?.show ? input[variant].error.color : input[variant].focus.color};
		-webkit-text-fill-color: ${({ variant, error, theme: { input } }) =>
			error?.show ? input[variant].error.color : input[variant].focus.color};
	}

	&::placeholder {
		color: ${({ variant, error, theme: { input } }) =>
			error?.show ? input[variant].error.placeholderColor : input[variant].placeholderColor};
	}

	&:focus::placeholder {
		color: ${({ variant, error, theme: { input } }) =>
			error?.show ? input[variant].error.placeholderColor : input[variant].focus.placeholderColor};
	}

	&:disabled {
		border: 1.5px solid ${({ variant, theme: { input } }) => input[variant].disabled.border};
		color: ${({ variant, theme: { input } }) => input[variant].disabled.color};
		-webkit-text-fill-color: ${({ variant, theme: { input } }) => input[variant].disabled.color};
		opacity: 1;
		-html-opacity: 1;
		-webkit-opacity: 1;
	}

	&:disabled::placeholder {
		color: ${({ variant, theme: { input } }) => input[variant].disabled.placeholderColor};
	}
`;

export const Adornment = styled(Icon)<Required<Pick<InputProps, 'variant'>>>`
	position: absolute;
	left: 17px;
	top: 25%;
	fill: ${({ variant, theme: { input } }) => input[variant].fontColor};
`;

export const Label = styled(Caption)`
	margin-bottom: 8px;

	& sup {
		color: ${({
			theme: {
				colors: { error },
			},
		}) => error};
	}
`;

export const ErrorLabel = styled.span<{ bottom: string; top?: string }>`
	position: absolute;
	bottom: ${({ bottom }) => bottom};
	top: ${({ top }) => top};
	left: 0;
	font-size: 12px;
	font-weight: 400;
	color: ${({ theme: { colors } }) => colors.error};
`;
